import React from "react";
import { getPathwayStyling, resolvePathwayKey } from "@/shared/utils/stylingConfigOperations";
import { DEFAULT_STYLING_CONFIG } from "@/types/stylingConfig";

const HK_GRADIENT =
  DEFAULT_STYLING_CONFIG.pathways!.hk!.modals!.borderBackground!;
const BK_GRADIENT =
  DEFAULT_STYLING_CONFIG.pathways!.bk!.modals!.borderBackground!;

export const getPopupGradient = (track?: string): string => {
  const pathway = resolvePathwayKey({ trackTitle: track });
  return getPathwayStyling(pathway).modals?.borderBackground ?? (pathway === "bk" ? BK_GRADIENT : HK_GRADIENT);
};

export const getPopupGradientFromTrackKey = (trackKey?: string): string => {
  const pathway = resolvePathwayKey({ trackKey });
  return getPathwayStyling(pathway).modals?.borderBackground ?? (pathway === "bk" ? BK_GRADIENT : HK_GRADIENT);
};

export const PopupContent: React.FC<{
  title: string;
  message: React.ReactNode;
  buttonText: string;
  onButtonClick: () => void;
  gradientStyle: string;
}> = ({ title, message, buttonText, onButtonClick, gradientStyle }) => (
  <>
    <div className="tw-text-center tw-pt-[8px] tw-px-[36px] tw-mb-[20px]">
      <h2
        className="tw-text-white tw-text-[20px] lg:tw-text-[24px] tw-font-[700]"
        style={{
          background: gradientStyle,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "var(--booking-modal-title-color, #ffffff)",
        }}
      >
        {title}
      </h2>
    </div>
    <div
      className="tw-text-white tw-text-[14px] lg:tw-text-[16px] tw-text-center tw-mb-[16px] tw-leading-relaxed"
      style={{ color: "var(--booking-modal-body-color, #ffffff)" }}
    >
      {message}
    </div>
    <button
      className="tw-mx-auto tw-flex tw-justify-center tw-items-center tw-w-fit tw-py-[12px] tw-px-[32px] tw-font-bold tw-text-[16px] tw-rounded-[100px] tw-text-white tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-opacity-90"
      style={{
        background: "var(--booking-modal-btn-bg, " + gradientStyle + ")",
        borderRadius: "var(--booking-modal-btn-radius, 100px)",
        fontSize: "var(--booking-modal-btn-font-size, 16px)",
      }}
      onClick={onButtonClick}
    >
      {buttonText}
    </button>
  </>
);

interface UnifiedPopupProps {
  isOpen: boolean;
  onClose: () => void;
  track?: string;
  trackKey?: string;
  maxWidth?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  isClosing?: boolean;
  onClosingChange?: (closing: boolean) => void;
}


// Unified popup component matching the sessions election automation popup

export const UnifiedPopup: React.FC<UnifiedPopupProps> = ({
  isOpen,
  onClose,
  track,
  trackKey,
  maxWidth = "500px",
  children,
  showCloseButton = true,
  isClosing: controlledIsClosing,
  onClosingChange,
}) => {
  const [internalClosing, setInternalClosing] = React.useState(false);
  const isClosing = controlledIsClosing ?? internalClosing;

  const gradientStyle = track ? getPopupGradient(track) : trackKey ? getPopupGradientFromTrackKey(trackKey) : HK_GRADIENT;

  const handleClose = React.useCallback(() => {
    if (isClosing) return;
    setInternalClosing(true);
    onClosingChange?.(true);
    setTimeout(() => {
      onClose();
      setInternalClosing(false);
      onClosingChange?.(false);
    }, 300);
  }, [onClose, isClosing, onClosingChange]);

  if (!isOpen) return null;

  return (
    <div
      className={`tw-fixed tw-z-[1000] tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-transition-all tw-duration-300 tw-bg-gray-900 ${
        isClosing ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
      }`}
      onClick={handleClose}
    >
      <div
        className={`tw-w-full tw-mx-auto tw-rounded-[16px] tw-p-[2px] tw-transform tw-transition-all tw-duration-300 ${
          isClosing ? "tw-scale-95 tw-opacity-0" : "tw-scale-100 tw-opacity-100"
        }`}
        style={{
          background: "var(--booking-modal-border-bg, " + gradientStyle + ")",
          borderRadius: "var(--booking-modal-radius, 16px)",
          maxWidth,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="tw-bg-black tw-rounded-[14px] tw-p-[20px] tw-relative"
          style={{ background: "var(--booking-modal-content-bg, #000000)" }}
        >
          {showCloseButton && (
            <button
              className="tw-absolute tw-top-[10px] tw-right-[10px] tw-text-gray-400 hover:tw-text-white tw-transition-colors tw-p-2"
              onClick={handleClose}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M19.1183 1.05664L1.48218 18.6928" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.124 18.7039L1.47314 1.04932" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
